import { apiClient, AUTH_TOKEN_KEY } from './client';
import type { AuthResponse, LoginPayload, RegisterPayload, User } from '@/types/domain';

/**
 * Endpoints d'authentification (cf wiki §14.2). Verifie via reflection sur le DLL
 * KSAdmin.Web 2.16.1 (Lot 7-prep) -- KStone n'utilise PAS de prefixe /api/auth/,
 * les routes sont exposees telles quelles a la racine :
 *   POST  /authentication_token        -> login (cf AuthController.Login)
 *   POST  /register                    -> creation de compte
 *   POST  /auth/logout                 -> invalidation cookie + token
 *   POST  /refresh-token               -> rafraichissement access token
 *   GET   /auth/token                  -> get token depuis cookie (SignalR)
 *   POST  /revoke/{stamp}/{conn}       -> revoke token specifique
 *   POST  /revoke-all                  -> revoke tous les tokens du joueur
 *   GET   /validate-email              -> valider email par code
 *   POST  /resend-validation-email/{id}-> renvoyer mail de validation
 *
 * Il n'existe pas de /me cote backend : la strategie utilisee ici est
 * de decoder l'access token JWT pour extraire le user_id puis d'appeler
 * GET /users/{id} (cf KSAdmin UsersController route "users").
 */

const LOGIN_PATH = '/authentication_token';
// Inscription : contrôleur theho2 dédié (anti-bot + confirmation email), PAS le
// /register brut du package (cf TheHoAuthController). Le compte est créé en
// statut Pending : aucun token renvoyé, le joueur valide son email d'abord.
const REGISTER_PATH = '/api/auth/register';
const RESEND_VALIDATION_PATH = '/api/auth/resend-validation';
const REFRESH_PATH = '/refresh-token';
const LOGOUT_PATH = '/auth/logout';
const USERS_PATH = '/users';

export async function login(payload: LoginPayload): Promise<AuthResponse> {
  // KSAdmin AuthController deserialise dans Credential { Username, Password }.
  // Notre type domain front utilise `login` pour parler "humain", on mappe ici.
  const body = { username: payload.login, password: payload.password };
  const { data } = await apiClient.post<RawAuthResponse>(LOGIN_PATH, body);
  if (!data?.success || !data.result) {
    throw new Error(data?.errors?.join(', ') || 'Authentification refusee.');
  }
  return { token: data.result.token, user: normalizeUser(data.result.user) };
}

/**
 * Crée le compte via le contrôleur theho2 (anti-bot + confirmation email). Ne
 * renvoie PAS de token : le compte est en attente de validation d'email. Le
 * back répond { message }. Le joueur doit valider son email avant de pouvoir se
 * connecter (le login du package refuse un compte non validé).
 */
export async function register(payload: RegisterPayload): Promise<{ message: string }> {
  const body = {
    email: payload.email,
    password: payload.password,
    pseudo: payload.login,
    captchaToken: payload.captchaToken ?? null,
    website: payload.website ?? null, // honeypot
  };
  const { data } = await apiClient.post<{ message?: string }>(REGISTER_PATH, body);
  return { message: data?.message ?? 'Compte créé. Vérifie tes emails pour l\'activer.' };
}

/** Renvoie le mail de validation pour un email donné (réponse générique). */
export async function resendValidation(email: string): Promise<{ message: string }> {
  const { data } = await apiClient.post<{ message?: string }>(RESEND_VALIDATION_PATH, { email });
  return { message: data?.message ?? 'Si un compte existe, un email a été envoyé.' };
}

export async function refreshToken(refresh: string): Promise<AuthResponse> {
  const { data } = await apiClient.post<AuthResponse>(REFRESH_PATH, { refreshToken: refresh });
  return data;
}

/**
 * KStone n'expose pas /me : on decode le JWT pour extraire le user_id
 * (claim "sub" ou "nameid" selon la version) puis on appelle GET /users/{id}.
 * Le client decode sans valider la signature : c'est le backend qui valide
 * lors des appels protegees, on a juste besoin de l'id.
 */
export async function fetchMe(): Promise<User> {
  const token = readAuthToken();
  if (!token) throw new Error('Aucun token JWT en localStorage.');
  const userId = extractUserIdFromJwt(token);
  if (!userId) throw new Error('JWT sans claim user_id (sub/nameid).');
  const { data } = await apiClient.get<RawUser>(`${USERS_PATH}/${userId}`);
  return normalizeUser(data);
}

/**
 * Shape brute renvoyee par KSAdmin UsersController : le champ identifiant
 * humain s'appelle `username` (cf User.d.ts de @siclop/ksadmin-vue). Notre
 * domaine front utilise `login`, donc on normalise ici. emailValidated est
 * derive de l'enum emailValidationStatus ("Validated" → true).
 */
interface RawUser {
  id: number;
  username?: string;
  login?: string;
  email?: string;
  emailValidationStatus?: string | number;
  emailValidated?: boolean;
  locale?: string;
  avatarPath?: string | null;
}

interface RawAuthTokenModel {
  token: string;
  user: RawUser;
  refreshToken?: string;
  expiration?: string;
  connection?: string;
  resources?: string[];
}

/**
 * KStone wrappe systematiquement les payloads dans DataOperationResult :
 * { success, result, errors }. Le AuthController renvoie donc le AuthTokenModel
 * sous `result`, pas au top-level — d'ou ce wrapper.
 */
interface RawAuthResponse {
  success: boolean;
  result: RawAuthTokenModel;
  errors: string[];
}

function normalizeUser(raw: RawUser): User {
  return {
    id: raw.id,
    login: raw.login ?? raw.username ?? '',
    email: raw.email ?? '',
    emailValidated: raw.emailValidated ?? raw.emailValidationStatus === 'Validated',
    locale: raw.locale ?? 'fr-FR',
    avatarPath: raw.avatarPath ?? null,
  };
}

export async function logoutBackend(): Promise<void> {
  try {
    await apiClient.post(LOGOUT_PATH);
  } catch {
    // best-effort : ignore les erreurs (token deja expire, reseau coupe...)
  }
}

// -----------------------------------------------------------------------------
// Helpers internes JWT (sans dependance externe)
// -----------------------------------------------------------------------------

function readAuthToken(): string | null {
  return localStorage.getItem(AUTH_TOKEN_KEY);
}

function extractUserIdFromJwt(token: string): number | null {
  try {
    const payload = decodeJwtPayload(token);
    // Convention .NET : Microsoft.IdentityModel utilise "nameid" pour le user id ;
    // OpenIddict / generic JWT utilise "sub". On accepte les deux.
    const raw = payload['nameid'] ?? payload['sub'] ?? payload['user_id'];
    if (typeof raw === 'number') return raw;
    if (typeof raw === 'string') {
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Rôles portés par le JWT. .NET sérialise le claim de rôle sous « role »
 * (parfois l'URI longue ClaimTypes.Role) ; valeur string OU tableau. Sert au
 * front à gater l'accès admin (le backend reste l'autorité — [Authorize(Roles)]).
 */
export function getRolesFromToken(token: string | null): string[] {
  if (!token) return [];
  try {
    const p = decodeJwtPayload(token);
    const claim = p['role'] ?? p['roles']
      ?? p['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
    if (Array.isArray(claim)) return claim.map((r) => String(r));
    if (typeof claim === 'string') return [claim];
    return [];
  } catch {
    return [];
  }
}

function decodeJwtPayload(token: string): Record<string, unknown> {
  const parts = token.split('.');
  if (parts.length < 2) return {};
  const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const json = decodeURIComponent(
    atob(padded)
      .split('')
      .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join(''),
  );
  return JSON.parse(json) as Record<string, unknown>;
}
